import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "react-query";
import { Grid, Stack } from "@mui/material";
import { Add, Remove } from "@mui/icons-material";
import { DefaultPageSize } from "@/common/constants/constants";
import { withContext } from "@/common/utils/withContext";
import { useTranslation } from "react-i18next";
import {
  IInErrorAssignSitePropsModel,
  IInErrorAssignSiteStateModel,
} from "@/modules/user/shared/models/inError";
import store from "@/store";
// Components
import { EvesModal } from "@/common/components/molecules/modal/modal";
import { Content } from "@/common/components/templates/content/content";
import DataTable from "@/common/components/organisms/table/dataTable";
import { EvesButton } from "@/common/components/atoms/button/button";
import { SearchBox } from "@/common/components/searchBox";
import LookupComponent from "@/common/components/lookupComponent";
// Utils
import { LookupEntityType, UpdatedUserRole } from "@/common/enums/enums";
import { disableLoaderForOnce } from "@/common/utils/loadingService";
import { columnsType } from "@/common/models/model";
// Store
import { setLoader } from "@/store/app/reducer";
import {
  canAssignUsersSites,
  canUnassignUsersSites,
} from "@/common/utils/utils";
// Services
import { assignSite, getUserSites, unAssignSite } from "@/services/users";
import { setSiteAdmin, setSiteOwner } from "@/services/organizations";
import { getUserInformation } from "@/services/userProfile";

function UserAssignSite(props: IInErrorAssignSitePropsModel) {
  const [state, setStateData] = useState({
    search: "",
    limit: DefaultPageSize,
    sortDescending: false,
    sortFields: props.defaultSortColumn ? props.defaultSortColumn : "site.name",
    rows: [],
    totalRowsCount: 0,
    isSelectSiteModalOpen: false,
    siteAdmin: false,
    isSiteAdmin: false,
    isSiteOwner: false,
    siteAdminUserId: "",
    siteOwner: false,
    defaultPageSize: DefaultPageSize,
    selectedRows: [],
    page: 0,
    rowsPerPage: 1,
    defaultSortColumn: "site.name",
    selectedUserRole: "",
    userId: "",
    projectFields: [],
  } as IInErrorAssignSiteStateModel);

  const dataTableRef = useRef<any>();
  const searchBoxRef = useRef<any>();
  const isUserCanAssignUsersSites = canAssignUsersSites();
  const isUserCanUnassignUsersSites = canUnassignUsersSites();
  const { t: translate } = useTranslation();

  const columns: columnsType[] = [
    {
      id: "name",
      label: `${translate("invoices.site_name")}`,
      minWidth: 90,
      allowSort: true,
      sortKey: "site.name",
      maxWidth: 240,
      textAlign: "left",
      headerAlign: "left",
      projectField: "site.name",
    },
    {
      id: "address",
      label: `${translate("general.address")}`,
      minWidth: 150,
      allowSort: false,
      sortKey: "address",
      maxWidth: 250,
      textAlign: "left",
      headerAlign: "left",
      projectField: "site.address.address1",
    },
    {
      id: "city",
      label: `${translate("general.city")}`,
      minWidth: 60,
      allowSort: false,
      sortKey: "city",
      maxWidth: 120,
      textAlign: "left",
      headerAlign: "left",
      projectField: "site.address.city",
    },
    {
      id: "country",
      label: `${translate("general.country")}`,
      minWidth: 150,
      allowSort: false,
      sortKey: "country",
      maxWidth: 90,
      textAlign: "left",
      headerAlign: "left",
      projectField: "site.address.country",
    },
  ];

  const { refetch: fetchSites } = useQuery(
    "UserSites",
    () => {
      return getSites();
    },
    {
      enabled: false,
      onSettled: () => {
        store.dispatch(setLoader(false));
      },
      onSuccess: (data) => {
        if (data) {
          const resultData = data.result.map((res: any) => {
            const addr1 = res.site?.address?.address1?.trim() || "";
            const addr2 = res.site?.address?.address2?.trim() || "";
            const fullAddress = [addr1, addr2]
              .filter((a) => a !== "")
              .join(", ");
            return {
              ...res,
              address: fullAddress || "-", // use concatenated address
              city: res.site?.address?.city || "-",
              country: res.site?.address?.country || "-",
              name: res.site?.name || "-",
            };
          });

          setStateData((currentData) => ({
            ...currentData,
            rows: resultData,
            totalRowsCount: data.count,
            projectFields: data.projectFields && [...data.projectFields],
            selectedRows: state.selectedRows,
          }));
        }
      },
    }
  );

  const { refetch: fetchUsers } = useQuery(
    "UsersDetails",
    () => {
      return getUserInformation(props.userId);
    },
    {
      enabled: false,
      onSuccess: (data: any) => {
        setStateData((currentData) => {
          return {
            ...currentData,
            selectedUserRole: data.role,
          };
        });
      },
    }
  );

  const { mutate: assignSiteMutate } = useMutation(
    (siteIdsModel) => assignSite(siteIdsModel, props.userId),
    {
      onMutate: () => {
        store.dispatch(setLoader(true));
      },
      onSettled: () => {
        store.dispatch(setLoader(false));
      },
      onSuccess: (res: any) => {
        if (res.status === "Success") {
          props.notificationService?.notify({
            message: `${translate("users.update_sites_success")}`,
            type: "success",
          });
          setStateData((currentData) => {
            return {
              ...currentData,
              isSelectSiteModalOpen: false,
            };
          });
        } else {
          props.notificationService?.notify({
            message: `${translate("sites.update_users_error")}`,
            type: "error",
          });
        }
      },
      onError: () => {
        props.notificationService?.notify({
          message:
            "Assignment failed — please verify and try again or contact your administrator.",
          type: "error",
        });
      },
    }
  );

  const { mutate: unassignUserMutate } = useMutation(
    (assignSiteIdsModel) => unAssignSite(assignSiteIdsModel, props.userId),
    {
      onMutate: () => {
        store.dispatch(setLoader(true));
      },
      onSettled: () => {
        store.dispatch(setLoader(false));
      },
      onSuccess: (res: any) => {
        if (res.status === "Success") {
          dataTableRef?.current?.resetSelectedRows();
          setStateData((currentData) => {
            return {
              ...currentData,
              selectedRows: [],
            };
          });
          props.notificationService?.notify({
            message: `${translate("users.remove_sites_success")}`,
            type: "success",
          });
          fetchSites();
        } else {
          props.notificationService?.notify({
            message: `${translate("users.remove_sites_error")}`,
            type: "error",
          });
        }
      },
      onError: (error: any) => {
        props.notificationService?.notify({
          message:
            "Update failed — please verify and try again or contact your administrator.",
          type: "error",
        });
        console.error("Unassign site error:", error);
      },
    }
  );

  useEffect(() => {
    if (!state.isSelectSiteModalOpen) {
      fetchSites();
      fetchUsers();
    }
  }, [
    state.limit,
    state.isSelectSiteModalOpen,
    state.sortFields,
    state.search,
    state.sortDescending,
    state.isSiteAdmin,
    state.siteOwner,
  ]);

  const getSites = () => {
    const editSiteParams: any = {
      UserID: props.userId,
      Limit: state.limit,
      SortFields: state.sortFields,
    };

    if (state.search !== "") {
      editSiteParams["Search"] = state.search;
    }
    store.dispatch(setLoader(true));
    return getUserSites(editSiteParams);
  };

  const onAssignAddSite = () => {
    setStateData((currentData) => {
      return {
        ...currentData,
        isSelectSiteModalOpen: true,
      };
    });
  };

  const onAssignSelectSite = (sites: any) => {
    const siteIds = sites.map((asset: any) => asset.id);
    const siteIDsModel: any = { siteIDs: siteIds };

    assignSiteMutate(siteIDsModel, {
      onSuccess: () => {
        if (
          [UpdatedUserRole.SITE_OWNER, UpdatedUserRole.AGENT].includes(
            state?.selectedUserRole as UpdatedUserRole
          )
        ) {
          sites.forEach((site: any) => {
            setSiteOwner({ siteOwner: true, userID: props.userId }, site.id);
          });
        }

        if (state.selectedUserRole === UpdatedUserRole.SITE_ADMIN) {
          sites.forEach((site: any) => {
            setSiteAdmin({ siteAdmin: true, userID: props.userId }, site.id);
          });
        }
      },
    });
  };

  const onAssignRemoveSitePopup = () => {
    props.dialogService?.showConfirm(
      {
        title: `${translate("users.remove_sites_title")}`,
        description: `${translate("users.remove_sites_confirm")}`,
        confirmType: "YES_NO",
      },
      (result: any) => {
        if (result === "YES") {
          if (state.selectedRows.length > 0) {
            // Extract site IDs from selected rows
            const siteIDs = state.selectedRows.map((row: any) => row?.site?.id);
            const assignSiteIdsModel: any = {
              siteIDs: siteIDs,
            };
            unassignUserMutate(assignSiteIdsModel);
          }
        }
      }
    );
  };

  const onRowSelectionChange = (selectedRows: any[]) => {
    setStateData((currentData) => {
      return {
        ...currentData,
        selectedRows: selectedRows,
      };
    });
  };

  const onSelectSiteModalClose = () => {
    setStateData((currentData) => {
      return {
        ...currentData,
        isSelectSiteModalOpen: false,
      };
    });
  };

  const onFilterApplied = (searchText: string) => {
    setStateData((currentData) => {
      return {
        ...currentData,
        search: searchText,
      };
    });
  };

  const onDataTableChange = (params: any) => {
    setStateData((currentData) => {
      return {
        ...currentData,
        sortFields: params.sortFields,
        limit: params.limit,
      };
    });
  };

  const onOwnerChange = async (record: any, event: any) => {
    const siteOwner = event.target.checked;
    const siteOwnerModel = {
      siteOwner: siteOwner ? true : false,
      userID: record.userID,
    };

    state.rows.map((userData) => {
      if (userData.site.id === record.site.id) {
        disableLoaderForOnce();
        userData.siteOwner = !userData.siteOwner;
      }
    });

    setSiteOwner(siteOwnerModel, record.site.id).then((res: any) => {
      disableLoaderForOnce();
      if (res.status === "Success") {
        props.notificationService?.notify({
          message: `The user is ${siteOwnerModel.siteOwner ? "now" : "no more"
            } owner of the site '${record.site.name}'`,
          type: "success",
        });
      } else {
        props.notificationService?.notify({
          message: `${translate("sites.update_error")}`,
          type: "error",
        });
      }
    });
  };

  const onAdminChange = (record: any, event: any) => {
    const siteAdmin = event.target.checked;
    const siteAdminModel = {
      siteAdmin: siteAdmin ? true : false,
      userID: record.userID,
    };

    state.rows.map((userData) => {
      if (userData.site.id === record.site.id) {
        disableLoaderForOnce();
        userData.siteAdmin = !userData.siteAdmin;
      }
    });

    setSiteAdmin(siteAdminModel, record.site.id).then((res: any) => {
      disableLoaderForOnce();
      if (res.status === "Success") {
        props.notificationService?.notify({
          message: `The user is ${siteAdmin ? "now" : "no nore"
            } administrator on the site '${record.site.name}'`,
          type: "success",
        });
      } else {
        props.notificationService?.notify({
          message: `${translate("sites.update_error")}`,
          type: "error",
        });
      }
    });
  };

  return (
    <EvesModal
      maxWidth={"1100px"}
      isOpen={props.showModal}
      title={`SITE ASSIGNED TO '${props.firstName} ${props.userName}'`}
      onHandleClose={props.onClose}
      isSaveDisabled={!state.selectedRows.length}
      isSaveHidden={true}
      data-cy="site-assign-modal"
    >
      <Content
        contentTop={
          <Grid container direction="row" spacing={2}>
            <Grid item xs={6}>
              <Stack direction="row" spacing={1}>
                {isUserCanAssignUsersSites && (
                  <EvesButton
                    startIcon={<Add />}
                    onClick={onAssignAddSite}
                    data-cy="site-editUser-add-btn"
                  >
                    {translate("general.add")}
                  </EvesButton>
                )}
                {isUserCanUnassignUsersSites && (
                  <EvesButton
                    color="error"
                    disabled={state.selectedRows.length > 0 ? false : true}
                    startIcon={<Remove />}
                    onClick={onAssignRemoveSitePopup}
                    data-cy="site-editUser-remove-btn"
                  >
                    {translate("general.remove")}
                  </EvesButton>
                )}
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <SearchBox
                ref={searchBoxRef}
                onChange={onFilterApplied}
                onRemoveClick={fetchSites}
                data-cy="site-editUser-search-textbox"
              />
            </Grid>
          </Grid>
        }
        contentBody={
          <DataTable
            ref={dataTableRef}
            selectionMode={"multiple"}
            rows={state.rows}
            columns={columns}
            totalRowsCount={state.totalRowsCount}
            onChange={onDataTableChange}
            height={{ height: "calc(100vh - 325px)" }}
            selectedRows={state.selectedRows}
            defaultSortField={state.defaultSortColumn}
            onRowSelectionChange={onRowSelectionChange}
            projectFields={state.projectFields}
            onOwnerChange={onOwnerChange}
            onAdminChange={onAdminChange}
            data-cy="assign-site-table"
          ></DataTable>
        }
      ></Content>
      {state.isSelectSiteModalOpen && (
        <LookupComponent
          title={translate("sites.select_sites")}
          showLookupModal={state.isSelectSiteModalOpen}
          onModalClose={onSelectSiteModalClose}
          selectionMode="multiple"
          entityType={LookupEntityType.SELECT_USERSITE}
          params={[
            { key: "Issuer", value: true },
            { key: "ExcludeSitesOfUserID", value: props.userId },
          ]}
          onSelect={onAssignSelectSite}
          data-cy="in-error-assign-lookup"
        />
      )}
    </EvesModal>
  );
}

export default withContext(UserAssignSite);
